import React, { useEffect, useState } from 'react';
import {
    Box, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatPhone, personName } from '../../voice/callFormat';
import { missedCallsApi, errorMessage } from './missedCallsApi';
import { FIELD_LABEL, eventText, formatDateTime, uploadedDay } from './missedCallFormat';

const TextBlock = ({ label, text }) => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', opacity: text ? 1 : 0.4 }}>{text || '(비어 있음)'}</Typography>
    </Box>
);

/**
 * A row's history (GET /missed-calls/:id/events): who did what and when — imports, calls, texts, 담당자,
 * 완료, and every edit of 내용 / 담당자 연결 / 처리현황 with the old and new text. Refreshes while open when the
 * row changes.
 */
const HistoryDialog = ({ row, open, onClose }) => {
    const [events, setEvents] = useState(null);
    const [error, setError] = useState(null);
    const rowId = row?.id;
    const rowVersion = row?.updatedAt;

    useEffect(() => {
        if (!open || !rowId) return undefined;
        let cancelled = false;
        setError(null);
        missedCallsApi.events(rowId)
            .then((data) => { if (!cancelled) setEvents(data.events); })
            .catch((err) => { if (!cancelled) setError(errorMessage(err)); });
        return () => { cancelled = true; };
    }, [open, rowId, rowVersion]);

    useEffect(() => { if (!open) setEvents(null); }, [open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" disableEnforceFocus>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                    기록
                    {row && (
                        <Typography variant="body2" color="text.secondary">
                            {uploadedDay(row)} #{row.daily_seq} · {formatPhone(row.phone_number)}
                        </Typography>
                    )}
                </Box>
                <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Typography color="error">{error}</Typography>}
                {!events && !error && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
                {events && events.length === 0 && <Typography color="text.secondary">기록이 없습니다.</Typography>}
                {events && events.map((e, i) => (
                    <Box key={e.id} sx={{ py: 1 }}>
                        {i > 0 && <Divider sx={{ mb: 1 }} />}
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {personName(e.admin) || '시스템'}
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{formatDateTime(e.createdAt)}</Typography>
                        </Typography>
                        <Typography variant="body2">{eventText(e)}</Typography>
                        {e.event === 'EDITED' && (
                            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                                <TextBlock label={`이전 ${FIELD_LABEL[e.field] || ''}`} text={e.old_value} />
                                <TextBlock label="수정 후" text={e.new_value} />
                            </Box>
                        )}
                        {(e.event === 'SMS_SENT' || e.event === 'SMS_FAILED' || e.event === 'SMS_SKIPPED_DEV') && e.meta?.message && (
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{e.meta.message}</Typography>
                        )}
                    </Box>
                ))}
            </DialogContent>
        </Dialog>
    );
};

export default HistoryDialog;
